import React, { useCallback, useEffect, useRef } from 'react';
import { Link2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

import { ConstructorView } from './constructor/ConstructorView';
import { InteractiveView } from './interactive/InteractiveView';
import type { RackOption } from './constructor/lineModel';

/**
 * V1 formatted editor over a daily-report `body_html`.
 *
 * Validators complained they can't edit raw HTML and want a formatted editor, so
 * the default tab is a WYSIWYG `contentEditable` where links render as links and
 * blockquotes as styled blocks — no angle-bracket tags to type. The report text is
 * **line-delimited** (each `\n` is one report row; see `ReportBody.tsx`), so the
 * round-trip is line-based: one top-level block ⇒ one line. A "raw" tab exposes the
 * exact `body_html` that will be saved — the transparency safety-net for prod text
 * that goes to Telegram, and the escape hatch for edits the WYSIWYG can't express.
 *
 * Controlled: the parent owns `body_html`; this only surfaces edits via `onChange`.
 */

// body_html (one line per row) → contentEditable HTML (one block per row).
function toEditorHtml(body: string): string {
  return body
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('<blockquote')) return line; // already a block element
      if (trimmed === '') return '<div><br></div>';
      return `<div>${line}</div>`;
    })
    .join('');
}

// contentEditable DOM → body_html. Each top-level block child becomes one line so
// the line-based format (and its `\n` joins) is preserved exactly.
function serializeEditor(root: HTMLElement): string {
  const lines: string[] = [];
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      lines.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      lines.push('');
      return;
    }
    if (el.tagName === 'BLOCKQUOTE') {
      lines.push(`<blockquote>${el.innerHTML}</blockquote>`);
      return;
    }
    // A DIV/P line wrapper — an empty line serialises to '' (not '<br>').
    const html = el.innerHTML === '<br>' ? '' : el.innerHTML;
    lines.push(html);
  });
  return lines.join('\n');
}

export type EditorMode = 'constructor' | 'interactive' | 'rich' | 'raw';

export function FormattedBodyEditor({
  value,
  onChange,
  day,
  zoneId,
  racksByType,
  openTime,
  maxCashiers,
  hiddenSections,
  onToggleSection,
  disabled,
  mode,
  onModeChange,
  onOpenRack,
  hiddenRows,
  onToggleRow,
}: {
  value: string;
  onChange: (bodyHtml: string) => void;
  day: string;
  zoneId: string;
  racksByType: Record<string, RackOption[]>;
  openTime: string | null;
  maxCashiers: number | null;
  hiddenSections: Set<string>;
  onToggleSection: (key: string) => void;
  disabled?: boolean;
  /** Controlled — the mode selector lives in the parent's top action bar now. */
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  /** Constructor category-link → consult-panel rack select (see ConstructorView). */
  onOpenRack?: (rackName: string | undefined, window: [number, number] | undefined) => void;
  /** Rows/blocks removed in the constructor (keyed by text) + toggle. */
  hiddenRows?: Set<string>;
  onToggleRow?: (key: string) => void;
}) {
  const { t } = useT();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const focusedRef = useRef(false);
  // The last html this component emitted, to distinguish self-originated changes
  // (don't re-seed the DOM, it would jump the caret) from external ones (do).
  const lastEmittedRef = useRef<string | null>(null);

  // Seed / re-seed the contentEditable when entering rich mode or when `value`
  // changes from the outside (e.g. discard/reset). Never while the user is typing.
  useEffect(() => {
    if (mode !== 'rich' || !editorRef.current) return;
    if (focusedRef.current) return;
    if (value === lastEmittedRef.current) return;
    editorRef.current.innerHTML = toEditorHtml(value);
  }, [value, mode]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const html = serializeEditor(editorRef.current);
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange]);

  const insertLink = useCallback(() => {
    if (disabled) return;
    const url = window.prompt(t('reportValidation.editor.linkPrompt'));
    if (!url) return;
    const sel = window.getSelection();
    if (sel && sel.toString().length > 0) {
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand(
        'insertHTML',
        false,
        `<a href="${url}">${url}</a>`,
      );
    }
    handleInput();
  }, [disabled, handleInput, t]);

  const toggleBold = useCallback(() => {
    if (disabled) return;
    document.execCommand('bold');
    handleInput();
  }, [disabled, handleInput]);

  // Don't navigate away when a link inside the editor is clicked while editing.
  const handleClick = useCallback((e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (anchor) e.preventDefault();
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <Tabs value={mode} onValueChange={(v) => onModeChange(v as EditorMode)}>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {mode === 'rich' && (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleBold}
                disabled={disabled}
              >
                <strong>B</strong>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={insertLink}
                disabled={disabled}
              >
                <Link2 className="size-3.5" />
                {t('reportValidation.editor.link')}
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="constructor">
          <ConstructorView
            value={value}
            onChange={onChange}
            day={day}
            racksByType={racksByType}
            openTime={openTime}
            maxCashiers={maxCashiers}
            hiddenSections={hiddenSections}
            onToggleSection={onToggleSection}
            disabled={disabled}
            onOpenRack={onOpenRack}
            hiddenRows={hiddenRows}
            onToggleRow={onToggleRow}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('reportValidation.constructor.hint')}
          </p>
        </TabsContent>

        <TabsContent value="interactive">
          <InteractiveView
            value={value}
            onChange={onChange}
            day={day}
            zoneId={zoneId}
            openTime={openTime}
            maxCashiers={maxCashiers}
            hiddenSections={hiddenSections}
            onToggleSection={onToggleSection}
            disabled={disabled}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('reportValidation.interactive.hint')}
          </p>
        </TabsContent>

        <TabsContent value="rich">
          <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={handleInput}
            onFocus={() => (focusedRef.current = true)}
            onBlur={() => (focusedRef.current = false)}
            onClick={handleClick}
            className={cn(
              'report-body min-h-40 rounded-md border border-border bg-background p-3',
              'text-sm leading-relaxed text-foreground outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring',
              disabled && 'pointer-events-none opacity-60',
            )}
          />
        </TabsContent>

        <TabsContent value="raw">
          <Textarea
            value={value}
            onChange={(e) => {
              lastEmittedRef.current = e.target.value;
              onChange(e.target.value);
            }}
            disabled={disabled}
            spellCheck={false}
            className="min-h-40 font-mono text-xs leading-relaxed"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
